import React, { useRef } from 'react'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Link from '@material-ui/core/Link'
import makeStyles from '@material-ui/core/styles/makeStyles'
import gql from 'graphql-tag'
import {
  checkRegistrationWindow,
  formatDateRange,
  longMonthDayFormat,
} from 'lib/family-cup-utils/event-dates'
import { MY_PGA_COM_HOST } from 'env'
import RegisterWithAccessCode from 'components/program-marketing/registration-card/register-with-access-code'
import HelpSvg from 'images/icons/fc-marketing/help.svg'
import {
  OfflinePayment,
  Label,
  Value,
  useFitCardToScreen,
} from 'components/program-marketing/registration-card'
import {
  JRL_REGISTRATION_STATUS_LABELS,
  JRL_REGISTRATION_WITH_CODE,
} from 'lib/junior-league-utils/constants'
import clsx from 'clsx'
import { DateTime } from 'luxon'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { getOrdinalPostfix } from 'components/league-standings/utils'

export const JRL_VALID_REGISTRATION_CODE = gql`
  query jrlProgramRegistrationCodeValid($id: ID!, $registrationCode: String!) {
    programRegistrationCodeValid: jrlProgramRegistrationCodeValid(
      id: $id
      registrationCode: $registrationCode
    ) {
      success
      message
    }
  }
`

const useStyles = makeStyles(theme => ({
  grayBox: {
    background: '#EBEBEB',
    borderRadius: '5px',
    padding: '16px',
    textAlign: 'center',
  },
  card: {
    border: '1px solid #DBDCDD',
    boxSizing: 'border-box',
    boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.12)',
    borderRadius: '10px',
    [theme.breakpoints.up('md')]: {
      top: theme.spacing(12),
      position: 'sticky',
    },
  },
  smallScreenCard: {
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
    },
  },
  row: {
    width: '100%',
    boxShadow: 'none',
    boxSizing: 'border-box',
    cursor: 'pointer',
    fontSize: '16px',
    lineHeight: '21px',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
}))

// TODO: Remove after Feb 1, 2022
const pastNationalRegOpenTime = dateTimeStr => {
  const dateTime = dateTimeStr || '2022-02-01T11:00:00-05:00'
  const currentESTTime = DateTime.fromObject({
    zone: 'America/New_York',
  })
  const openTime = DateTime.fromISO(dateTime)
  return currentESTTime >= openTime
}

const DisabledButton = ({ children }) => {
  const classes = useStyles()
  return (
    <Box mb={3}>
      <Box className={classes.grayBox}>
        <Typography
          variant="h6"
          color="textPrimary"
          style={{
            fontFamily: 'PlusJakarta Text',
          }}
        >
          {children}
        </Typography>
      </Box>
    </Box>
  )
}

const getRegistrationPeriod = ({ registrationOpen, registrationClose }) => {
  const range = formatDateRange(registrationOpen, registrationClose, {
    dateFormat: longMonthDayFormat,
    sameYearDateFormat: longMonthDayFormat,
  })

  if (!registrationOpen) {
    return `Ends ${range}`
  }

  return range
}

const RegistrationButton = ({
  registrationIsOpen,
  afterRegistrationOpen,
  registrationStatus,
  viewAccessCodeRequired,
  registrationFull,
  registrationOpenings,
  programId,
  afterNationalRegOpenTime,
  nationalOpenDateTimeStr,
}) => {
  const registerUrl = `${MY_PGA_COM_HOST}/junior-league-registration/${programId}`
  const showSpotsWarning =
    !registrationFull && registrationOpenings <= 5 && registrationOpenings > 0
  const isPlural = registrationOpenings > 1 ? 's' : ''
  const spotsWarning = `only ${registrationOpenings} spot${isPlural} remaining`
  const nationalOpenDateTime = DateTime.fromISO(nationalOpenDateTimeStr)
  if (registrationIsOpen) {
    return (
      <>
        <Box mb={3}>
          {showSpotsWarning && (
            <Typography color="error">{spotsWarning}</Typography>
          )}
          {JRL_REGISTRATION_WITH_CODE[registrationStatus] ? (
            <RegisterWithAccessCode
              viewAccessCodeRequired={viewAccessCodeRequired}
              registerUrl={registerUrl}
              programId={programId}
              codeValidationQuery={JRL_VALID_REGISTRATION_CODE}
            />
          ) : (
            <Button
              component={Link}
              href={registerUrl}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth
              variant="contained"
              color="primary"
            >
              Register Player
            </Button>
          )}
        </Box>
      </>
    )
  }

  if (afterRegistrationOpen && !afterNationalRegOpenTime) {
    const dayOfWeekOrdinal = getOrdinalPostfix(
      parseInt(nationalOpenDateTime.toFormat('d')),
    )
    return (
      <DisabledButton>
        Registration opens {nationalOpenDateTime.toFormat('LLL. d')}
        {dayOfWeekOrdinal}
        <br />
        at {nationalOpenDateTime.toFormat('h:mm a ZZZZ')}
      </DisabledButton>
    )
  }

  if (afterRegistrationOpen || registrationFull) {
    return (
      <>
        <Box mb={1}>
          <Typography variant="caption">
            {registrationFull ? (
              <span>Registration for this event has reached capacity.</span>
            ) : (
              <span>The registration window for this event has closed.</span>
            )}
          </Typography>
        </Box>
        <DisabledButton>Registration Closed</DisabledButton>
      </>
    )
  }

  if (!afterRegistrationOpen) {
    return <DisabledButton>Registration Not Started</DisabledButton>
  }
}

export const RegistrationCard = ({
  program,
  scheduleLabel,
  viewAccessCodeRequired,
  viewTeamEligibility,
  viewAnnualMembership,
  viewRegistrationType,
}) => {
  const classes = useStyles()
  const cardRef = useRef(null)
  const isScreenSmall = useFitCardToScreen({ cardRef })
  const [JRLGoLiveDateTime] = useFeatureFlags([flags.FLAG_JRL_GO_LIVE])

  const nationalOpenDateTimeStr =
    JRLGoLiveDateTime || '2022-02-01T11:00:00-05:00'
  const afterNationalRegOpenTime = pastNationalRegOpenTime(
    nationalOpenDateTimeStr,
  )

  const {
    id,
    coachingFeeInCents,
    collectOnline,
    membershipFeeInCents,
    registrationClose,
    registrationOpen,
    maxPlayers,
    registrationOpenings,
    registrationStatus,
  } = program

  const registrationFull = maxPlayers && !registrationOpenings

  const registrationPeriod = getRegistrationPeriod({
    registrationOpen,
    registrationClose,
  })

  const {
    inRegistrationWindow,
    afterRegistrationOpen,
  } = checkRegistrationWindow(registrationOpen, registrationClose)

  const registrationIsOpen =
    inRegistrationWindow && !registrationFull && afterNationalRegOpenTime
  const coachFee = coachingFeeInCents / 100
  const membershipFee = membershipFeeInCents / 100
  const totalFee = coachFee + membershipFee

  return (
    <Card
      data-testid="registration-card"
      className={clsx(classes.card, isScreenSmall && classes.smallScreenCard)}
      ref={cardRef}
    >
      <CardContent style={{ padding: '32px' }}>
        <Box mb={2}>
          <Label>Registration Period</Label>
          <Value>{registrationPeriod}</Value>
        </Box>

        <Box>
          <Label>Cost</Label>
          <Value>${totalFee} Per Player</Value>
        </Box>
        <Box className={classes.row} onClick={viewAnnualMembership} mb={2}>
          <Value>
            (${coachFee} Coach Fee + ${membershipFee} Annual Membership)
          </Value>
          <Box display="flex" alignItems="center" mr={1}>
            <img src={HelpSvg} alt="fee-help" />
          </Box>
        </Box>

        {!collectOnline && (
          <OfflinePayment onlineFee={membershipFee} offlineFee={coachFee} />
        )}

        <Box mb={2}>
          <Label>Schedule</Label>
          <Value>{scheduleLabel}</Value>
        </Box>

        <Box mb={5}>
          <Label>Registration Type</Label>
          <Box className={classes.row} onClick={viewRegistrationType}>
            <Value>{JRL_REGISTRATION_STATUS_LABELS[registrationStatus]}</Value>
            <Box display="flex" alignItems="center" mr={1}>
              <img src={HelpSvg} alt="fee-help" />
            </Box>
          </Box>
        </Box>

        <RegistrationButton
          programId={id}
          registrationIsOpen={registrationIsOpen}
          afterRegistrationOpen={afterRegistrationOpen}
          registrationStatus={registrationStatus}
          viewAccessCodeRequired={viewAccessCodeRequired}
          registrationFull={registrationFull}
          registrationOpenings={registrationOpenings}
          afterNationalRegOpenTime={afterNationalRegOpenTime}
          nationalOpenDateTimeStr={nationalOpenDateTimeStr}
        />

        <Box align="left">
          <Typography variant="caption" component="p">
            Must be 18 years or older to register
          </Typography>
          <Typography variant="caption" color="primary">
            <Link onClick={viewTeamEligibility}>
              View all team eligibility &amp; requirements
            </Link>
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}
